import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { sanitizeCmsHtml } from '@agentic/magento-client/lib/cms-sanitize';
import type { BlockComponentProps } from '../../renderer/BlockRegistry';

// A leading "[title: …]" / "[title = …]" marker in the CMS block lets a merchant
// set the popover's trigger label from the block itself — it takes precedence
// over the editor LABEL field, so the whole thing is editable in one place.
// Extract it, then strip the marker + any now-empty wrapping tag so it never
// renders inside the dropdown.
const MENU_TITLE_MARKER = /\[title\s*[:=]\s*([^\]]+)\]/i;
function extractMenuTitle(rawHtml: string): { title: string | null; html: string } {
    const m = rawHtml.match(MENU_TITLE_MARKER);
    if (!m) return { title: null, html: rawHtml };
    const html = rawHtml
        .replace(MENU_TITLE_MARKER, '')
        .replace(/<(p|h[1-6]|div)>\s*(?:&nbsp;|\s)*<\/\1>/gi, '');
    return { title: m[1].trim() || null, html };
}

export const HelpPopover: React.FC<BlockComponentProps> = ({ data, className, style, context }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [contentHtml, setContentHtml] = useState<string | null>(null);
    const [menuTitle, setMenuTitle] = useState<string | null>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });

    interface HelpPopoverContent { label?: string; cmsTextIdentifier?: string; identifier?: string; cmsContent?: string; variant?: string }
    const dataAny = data as undefined | { content?: HelpPopoverContent; settings?: { label?: string }; cmsContent?: string; variant?: string };
    const cmsIdentifier: string = dataAny?.content?.cmsTextIdentifier ?? dataAny?.content?.identifier ?? 'header_quick_link';
    const ssrContent: string | undefined = dataAny?.content?.cmsContent ?? dataAny?.cmsContent;
    // A "[title: …]" / "[title = …]" marker inside the CMS block wins over the
    // editor LABEL field. Parse SSR content synchronously so there's no label
    // flash; the synced-block path sets menuTitle via its effect below.
    const ssrTitle = ssrContent ? extractMenuTitle(ssrContent).title : null;
    const triggerLabel: string = ssrTitle ?? menuTitle ?? dataAny?.content?.label ?? dataAny?.settings?.label ?? 'Help & Support';

    // Variant: dropdown-menu (default — text trigger + dropdown) |
    //          icon-only (just a ? icon, click opens dropdown) |
    //          icon-text (? icon + text label + dropdown)
    const variantRaw = dataAny?.content?.variant ?? dataAny?.variant ?? 'dropdown-menu';
    const variant: 'dropdown-menu' | 'icon-only' | 'icon-text' =
        ['dropdown-menu','icon-only','icon-text'].includes(variantRaw)
            ? variantRaw as 'dropdown-menu' | 'icon-only' | 'icon-text'
            : 'dropdown-menu';

    // SSR content
    useEffect(() => {
        if (ssrContent) {
            setContentHtml(sanitizeCmsHtml(extractMenuTitle(ssrContent).html));
        }
    }, [ssrContent]);

    // Synced CMS content from context — NO live Magento fallback. Prebuilt/SSR
    // pages are built entirely from the CMS sync; if the help block isn't synced
    // the popover simply has no content rather than querying Magento for a
    // missing identifier (the "CMS block X doesn't exist" error source).
    useEffect(() => {
        if (ssrContent || contentHtml) return;
        interface CmsBlockEntry { identifier?: string; content?: string }
        const block = (context as { cmsBlocks?: CmsBlockEntry[] } | undefined)?.cmsBlocks
            ?.find((b) => b.identifier === cmsIdentifier);
        if (block?.content) {
            const { title, html } = extractMenuTitle(block.content);
            if (title) setMenuTitle(title);
            setContentHtml(sanitizeCmsHtml(html));
        }
    }, [cmsIdentifier, ssrContent, contentHtml, context]);

    // Position the portal dropdown below the trigger button
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPos({
            top: rect.bottom + 8,
            right: window.innerWidth - rect.right,
        });
    }, []);

    // Close on click outside (check both trigger and portal dropdown)
    useEffect(() => {
        if (!isOpen) return;
        updatePosition();
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                popoverRef.current && !popoverRef.current.contains(target) &&
                dropdownRef.current && !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        };
        const handleScroll = () => updatePosition();
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpen, updatePosition]);

    const fallbackHtml = `
        <div style="padding: 16px;">
            <p style="font-weight: 600; margin-bottom: 8px;">Customer Support</p>
            <p style="font-size: 13px; color: var(--text-secondary);">
                Create a CMS block with identifier <code style="background: var(--surface-2); padding: 2px 6px; border-radius: var(--style-badge-radius, 3px); font-size: 12px;">${cmsIdentifier}</code> to populate this area.
            </p>
        </div>
    `;

    return (
        <div
            ref={popoverRef}
            className={`relative inline-flex items-center ${className ?? ''}`}
            style={style}
        >
            {/* Variant: icon-only — just a circular help (?) icon button. */}
            {variant === 'icon-only' && (
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center justify-center transition-colors header-icon-btn"
                    style={{
                        width: 32,
                        height: 32,
                        borderRadius: 'var(--style-btn-radius, 9999px)',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: isOpen ? 'var(--accent-primary)' : 'var(--color-header-icon, currentColor)',
                    }}
                    aria-label={triggerLabel}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                    title={triggerLabel}
                >
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                </button>
            )}

            {/* Variant: icon-text — icon + label + chevron. */}
            {variant === 'icon-text' && (
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-2 transition-colors"
                    style={{
                        color: isOpen ? 'var(--accent-primary)' : 'color-mix(in srgb, var(--color-header-icon, var(--text-muted, #FFFFFF)) 70%, transparent)',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span className="hidden sm:inline">{triggerLabel}</span>
                    <svg
                        className="w-3 h-3 opacity-60 transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            )}

            {/* Variant: dropdown-menu (default) — text trigger + chevron. */}
            {variant === 'dropdown-menu' && (
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="flex items-center gap-1 transition-colors"
                    style={{
                        color: isOpen ? 'var(--accent-primary)' : 'color-mix(in srgb, var(--color-header-icon, var(--text-muted, #FFFFFF)) 70%, transparent)',
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                    aria-haspopup="true"
                    aria-expanded={isOpen}
                >
                    {triggerLabel}
                    <svg
                        className="w-3 h-3 opacity-60 transition-transform duration-200"
                        style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
            )}

            {/* Portal: render dropdown at document.body to escape all stacking contexts */}
            {isOpen && typeof document !== 'undefined' && createPortal(
                <div
                    ref={dropdownRef}
                    className="overflow-hidden"
                    style={{
                        position: 'fixed',
                        top: `${dropdownPos.top}px`,
                        right: `${dropdownPos.right}px`,
                        zIndex: 99999,
                        width: 280,
                        backgroundColor: 'var(--style-modal-bg, var(--surface-1))',
                        border: '1px solid var(--glass-border)',
                        borderRadius: 'var(--style-modal-radius, var(--style-card-radius, 12px))',
                        boxShadow: 'var(--style-modal-shadow, 0 12px 40px rgba(0,0,0,0.2))',
                        backdropFilter: 'var(--style-backdrop-filter, blur(16px))',
                        WebkitBackdropFilter: 'var(--style-backdrop-filter, blur(16px))',
                    }}
                >
                    <div
                        className="text-sm help-popover-content"
                        style={{
                            padding: '16px',
                            color: 'var(--text-primary)',
                        }}
                        dangerouslySetInnerHTML={{ __html: contentHtml || fallbackHtml }}
                    />
                    {/* Contact link — always shown at bottom of popup */}
                    <a
                        href="/contact"
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px 16px',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'var(--color-accent-fg)',
                            textDecoration: 'none',
                            borderTop: '1px solid var(--glass-border)',
                            transition: 'background 0.15s ease',
                        }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--surface-2)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                        </svg>
                        Contact Us
                    </a>
                </div>,
                document.body
            )}
        </div>
    );
};

export default HelpPopover;
